import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ETLayout, euro } from "@/pages/et/ETLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus, Search, Pencil, TrendingUp, Upload, Mail, Loader2, AlertTriangle, CheckCircle2, Database, FileDown } from "lucide-react";

const empty = { nome: "", cognome: "", codice_fiscale: "", data_nascita: "", telefono: "", email: "", comune: "", indirizzo: "", centro_id: "", giorni: "", quota_mensile: 0, data_inizio: "", data_fine: "", stato: "attivo", note: "" };
const STATI = { attivo: "bg-emerald-100 text-emerald-700", sospeso: "bg-amber-100 text-amber-700", terminato: "bg-slate-200 text-slate-600" };

export default function ETBeneficiari() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [centri, setCentri] = useState([]);
  const [query, setQuery] = useState("");
  const [centroFilter, setCentroFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const [varOpen, setVarOpen] = useState(null);
  const [varData, setVarData] = useState({ quota_mensile: 0, decorrenza: "" });
  const [impOpen, setImpOpen] = useState(false);
  const [impFile, setImpFile] = useState(null);
  const [impReport, setImpReport] = useState(null);
  const [impBusy, setImpBusy] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);
  const [fullFile, setFullFile] = useState(null);
  const [fullReport, setFullReport] = useState(null);
  const [fullBusy, setFullBusy] = useState(false);
  const [cfOpen, setCfOpen] = useState(false);
  const [cfFile, setCfFile] = useState(null);
  const [cfReport, setCfReport] = useState(null);
  const [cfBusy, setCfBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = () => {
    const params = {};
    if (query) params.query = query;
    if (centroFilter !== "all") params.centro_id = centroFilter;
    api.get("/et/beneficiari", { params }).then(({ data }) => setItems(data));
  };
  useEffect(() => { api.get("/et/centri").then(({ data }) => setCentri(data)); }, []);
  useEffect(load, [centroFilter]); // eslint-disable-line

  const openNew = () => { setF({ ...empty }); setOpen(true); };
  const openEdit = (b) => { setF({ ...b }); setOpen(true); };
  const save = async () => {
    const payload = { ...f, quota_mensile: Number(f.quota_mensile) || 0 };
    const bid = payload.ben_id;
    ["ben_id", "created_at", "centro_name", "credito", "pending_quota", "pending_from"].forEach((k) => delete payload[k]);
    try {
      if (bid) await api.put(`/et/beneficiari/${bid}`, payload); else await api.post("/et/beneficiari", payload);
      toast.success("Beneficiario salvato"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveVar = async () => {
    try {
      await api.post(`/et/beneficiari/${varOpen.ben_id}/variazione-quota`, { quota_mensile: Number(varData.quota_mensile), decorrenza: varData.decorrenza });
      toast.success(`Nuova quota da ${varData.decorrenza}`); setVarOpen(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openImport = () => { setImpFile(null); setImpReport(null); setImpOpen(true); };
  const runImport = async (dry) => {
    if (!impFile) { toast.error("Seleziona un file Excel o CSV"); return; }
    setImpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", impFile);
      fd.append("dry_run", dry ? "true" : "false");
      const { data } = await api.post("/et/beneficiari/import-email", fd);
      setImpReport(data);
      if (!dry) { toast.success(`${data.aggiornati} email aggiornate`); setImpOpen(false); load(); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setImpBusy(false); }
  };

  const openFull = () => { setFullFile(null); setFullReport(null); setFullOpen(true); };
  const runFull = async (dry) => {
    if (!fullFile) { toast.error("Seleziona il file Excel completo"); return; }
    setFullBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", fullFile);
      fd.append("dry_run", dry ? "true" : "false");
      const { data } = await api.post("/et/import-full", fd);
      setFullReport(data);
      if (!dry) {
        toast.success("Importazione completata");
        setFullOpen(false);
        api.get("/et/centri").then(({ data: c }) => setCentri(c));
        load();
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setFullBusy(false); }
  };

  const openCf = () => { setCfFile(null); setCfReport(null); setCfOpen(true); };
  const runCf = async (dry) => {
    if (!cfFile) { toast.error("Seleziona un file Excel o CSV"); return; }
    setCfBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", cfFile);
      fd.append("dry_run", dry ? "true" : "false");
      const { data } = await api.post("/et/beneficiari/import-cf", fd);
      setCfReport(data);
      if (!dry) { toast.success(`${data.aggiornati} codici fiscali aggiornati`); setCfOpen(false); load(); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCfBusy(false); }
  };

  const missingCf = items.filter((b) => !b.codice_fiscale).length;
  const exportCfMancanti = async () => {
    try {
      const { data } = await api.get("/et/beneficiari/cf-mancanti/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "beneficiari_cf_mancanti.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore durante l'esportazione"); }
  };

  return (
    <ETLayout subtitle="Anagrafica" title="Beneficiari"
      actions={<div className="flex gap-2">
        {isAdmin && <Button data-testid="et-import-full-btn" variant="outline" onClick={openFull} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><Database className="h-4 w-4 mr-2" /> Importa dati storici</Button>}
        <Button data-testid="et-import-email-btn" variant="outline" onClick={openImport}><Upload className="h-4 w-4 mr-2" /> Importa email (Excel)</Button>
        <Button data-testid="et-import-cf-btn" variant="outline" onClick={openCf}><Upload className="h-4 w-4 mr-2" /> Importa Codici Fiscali</Button>
        {missingCf > 0 && <Button data-testid="et-export-cf-mancanti-btn" variant="outline" onClick={exportCfMancanti} className="border-amber-300 text-amber-700 hover:bg-amber-50"><FileDown className="h-4 w-4 mr-2" /> CF mancanti ({missingCf})</Button>}
        <Button data-testid="et-add-ben-btn" onClick={openNew} className="brand-gradient text-white border-0"><UserPlus className="h-4 w-4 mr-2" /> Nuovo beneficiario</Button>
      </div>}>
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="et-ben-search" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cerca per nome, cognome o CF…" className="pl-9 bg-white border-slate-300" />
        </div>
        <Select value={centroFilter} onValueChange={setCentroFilter}>
          <SelectTrigger className="w-56" data-testid="et-centro-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i centri</SelectItem>
            {centri.map((c) => <SelectItem key={c.centro_id} value={c.centro_id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load}>Cerca</Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Beneficiario</TableHead><TableHead>Centro</TableHead><TableHead>Email</TableHead><TableHead>Quota</TableHead>
            <TableHead>Credito</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">Nessun beneficiario.</TableCell></TableRow>
            ) : items.map((b) => (
              <TableRow key={b.ben_id} data-testid={`et-ben-row-${b.ben_id}`}>
                <TableCell><div className="font-medium text-slate-900">{b.cognome} {b.nome}</div>{b.codice_fiscale ? <div className="text-xs text-slate-500 font-mono">{b.codice_fiscale}</div> : <Badge data-testid={`et-ben-cf-missing-${b.ben_id}`} className="mt-0.5 bg-amber-100 text-amber-700 border-0 text-[10px] px-1.5 py-0 gap-1"><AlertTriangle className="h-3 w-3" /> CF mancante</Badge>}</TableCell>
                <TableCell className="text-sm text-slate-600">{b.centro_name || "—"}</TableCell>
                <TableCell className="text-sm text-slate-600">{b.email ? b.email : <span className="text-slate-300">—</span>}</TableCell>
                <TableCell className="text-sm">{euro(b.quota_mensile)}{b.pending_quota != null && <div className="text-[11px] text-amber-600">→ {euro(b.pending_quota)} da {b.pending_from}</div>}</TableCell>
                <TableCell className="text-sm">{b.credito > 0 ? <span className="text-emerald-600">{euro(b.credito)}</span> : "—"}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATI[b.stato] || ""}`}>{b.stato}</span></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setVarOpen(b); setVarData({ quota_mensile: b.quota_mensile, decorrenza: "" }); }} data-testid={`et-var-${b.ben_id}`} title="Variazione quota"><TrendingUp className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(b)} data-testid={`et-edit-ben-${b.ben_id}`}><Pencil className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {f && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">{f.ben_id ? "Modifica" : "Nuovo"} beneficiario</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              <Fld label="Nome"><Input value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} data-testid="et-ben-nome" /></Fld>
              <Fld label="Cognome"><Input value={f.cognome} onChange={(e) => setF({ ...f, cognome: e.target.value })} data-testid="et-ben-cognome" /></Fld>
              <Fld label="Codice fiscale"><Input value={f.codice_fiscale} onChange={(e) => setF({ ...f, codice_fiscale: e.target.value })} /></Fld>
              <Fld label="Data nascita"><Input type="date" value={f.data_nascita} onChange={(e) => setF({ ...f, data_nascita: e.target.value })} /></Fld>
              <Fld label="Telefono"><Input value={f.telefono} onChange={(e) => setF({ ...f, telefono: e.target.value })} /></Fld>
              <Fld label="Email"><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Fld>
              <Fld label="Comune"><Input value={f.comune} onChange={(e) => setF({ ...f, comune: e.target.value })} /></Fld>
              <Fld label="Indirizzo"><Input value={f.indirizzo} onChange={(e) => setF({ ...f, indirizzo: e.target.value })} /></Fld>
              <Fld label="Centro servizio">
                <Select value={f.centro_id} onValueChange={(v) => setF({ ...f, centro_id: v })}>
                  <SelectTrigger data-testid="et-ben-centro"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{centri.map((c) => <SelectItem key={c.centro_id} value={c.centro_id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
              <Fld label="Giorni trasporto"><Input value={f.giorni} onChange={(e) => setF({ ...f, giorni: e.target.value })} placeholder="Lun, Mer, Ven" /></Fld>
              <Fld label="Quota mensile (€)"><Input type="number" value={f.quota_mensile} onChange={(e) => setF({ ...f, quota_mensile: e.target.value })} data-testid="et-ben-quota" /></Fld>
              <Fld label="Stato">
                <Select value={f.stato} onValueChange={(v) => setF({ ...f, stato: v })}>
                  <SelectTrigger data-testid="et-ben-stato"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="attivo">Attivo</SelectItem><SelectItem value="sospeso">Sospeso</SelectItem><SelectItem value="terminato">Terminato</SelectItem></SelectContent>
                </Select>
              </Fld>
              <Fld label="Data inizio"><Input type="date" value={f.data_inizio} onChange={(e) => setF({ ...f, data_inizio: e.target.value })} /></Fld>
              <Fld label="Data fine"><Input type="date" value={f.data_fine} onChange={(e) => setF({ ...f, data_fine: e.target.value })} /></Fld>
              <div className="col-span-2"><Fld label="Note"><Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Fld></div>
            </div>
            <DialogFooter><Button data-testid="et-save-ben-btn" onClick={save} disabled={!f.nome || !f.cognome} className="brand-gradient text-white border-0">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={!!varOpen} onOpenChange={(o) => !o && setVarOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Variazione quota</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">La nuova quota si applica dalla competenza indicata; lo storico resta invariato.</p>
          <div className="grid grid-cols-2 gap-3 py-2">
            <Fld label="Nuova quota (€)"><Input type="number" value={varData.quota_mensile} onChange={(e) => setVarData({ ...varData, quota_mensile: e.target.value })} data-testid="et-var-quota" /></Fld>
            <Fld label="Decorrenza (mese)"><Input type="month" value={varData.decorrenza} onChange={(e) => setVarData({ ...varData, decorrenza: e.target.value })} data-testid="et-var-decorrenza" /></Fld>
          </div>
          <DialogFooter><Button onClick={saveVar} disabled={!varData.decorrenza} className="brand-gradient text-white border-0" data-testid="et-var-save">Registra variazione</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={impOpen} onOpenChange={setImpOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Importa email beneficiari</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-xs text-slate-500">Carica un file <b>Excel (.xlsx)</b> o <b>CSV</b> con una colonna per il <b>Nome/Cognome</b> e una per l'<b>Email</b>. Il sistema abbina i beneficiari per nome e mostra un'anteprima prima di salvare.</p>
            <input data-testid="et-import-file" type="file" accept=".xlsx,.csv"
              onChange={(e) => { setImpFile(e.target.files?.[0] || null); setImpReport(null); }}
              className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200" />

            {impReport && (
              <div className="space-y-2 text-sm" data-testid="et-import-report">
                <div className="grid grid-cols-2 gap-2">
                  <Stat label="Righe nel file" value={impReport.righe_totali} />
                  <Stat label="Abbinati con email" value={impReport.abbinati_con_email} cls="text-emerald-600" />
                  <Stat label="Non trovati" value={impReport.non_trovati.length} cls={impReport.non_trovati.length ? "text-amber-600" : ""} />
                  <Stat label="Ambigui / email non valide" value={impReport.ambigui.length + impReport.email_non_valide.length} cls={(impReport.ambigui.length + impReport.email_non_valide.length) ? "text-red-600" : ""} />
                </div>
                {impReport.abbinati_con_email > 0 && (
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-emerald-200 divide-y divide-emerald-50">
                    {impReport.abbinati.filter((a) => a.email_nuova).map((a) => (
                      <div key={a.ben_id} className="flex justify-between px-3 py-1.5 text-xs">
                        <span className="text-slate-700">{a.nome}</span>
                        <span className="font-medium text-emerald-700">{a.email_nuova}{a.email_attuale && a.email_attuale !== a.email_nuova ? " (sovrascrive)" : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
                {impReport.non_trovati.length > 0 && (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
                    <div className="flex items-center gap-1 font-medium mb-1"><AlertTriangle className="h-3.5 w-3.5" /> Non abbinati (nome non trovato)</div>
                    {impReport.non_trovati.join(", ")}
                  </div>
                )}
                {(impReport.ambigui.length > 0 || impReport.email_non_valide.length > 0) && (
                  <div className="rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-800">
                    {impReport.ambigui.length > 0 && <div>Ambigui (più beneficiari con lo stesso nome): {impReport.ambigui.map((a) => a.nome_file).join(", ")}</div>}
                    {impReport.email_non_valide.length > 0 && <div>Email non valide: {impReport.email_non_valide.map((a) => `${a.nome_file} (${a.email})`).join(", ")}</div>}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {!impReport ? (
              <Button data-testid="et-import-analizza" onClick={() => runImport(true)} disabled={!impFile || impBusy} className="brand-gradient text-white border-0">
                {impBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Analizza file"}
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setImpReport(null)}>Ricarica</Button>
                <Button data-testid="et-import-conferma" onClick={() => runImport(false)} disabled={impBusy || impReport.abbinati_con_email === 0} className="brand-gradient text-white border-0">
                  {impBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Conferma: salva ${impReport.abbinati_con_email} email`}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={fullOpen} onOpenChange={setFullOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Importa dati storici ANFFAS</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Carica il file Excel completo (Beneficiari, Paganti, Quote, Ricevute). La conferma <b>azzera</b> tutti i dati ANFFAS esistenti (beneficiari, centri, quote, ricevute, pagamenti) e reimporta da zero. Le impostazioni (Configurazione) restano invariate.</span>
            </div>
            <input data-testid="et-full-file" type="file" accept=".xlsx"
              onChange={(e) => { setFullFile(e.target.files?.[0] || null); setFullReport(null); }}
              className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200" />

            {fullReport && (
              <div className="space-y-2" data-testid="et-full-report">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <Stat label="Beneficiari" value={fullReport.riepilogo.beneficiari} />
                  <Stat label="Con email" value={fullReport.riepilogo.beneficiari_con_email} cls="text-emerald-600" />
                  <Stat label="Quote storiche" value={fullReport.riepilogo.quote} />
                  <Stat label="di cui pagate" value={fullReport.riepilogo.quote_pagate} cls="text-emerald-600" />
                  <Stat label="Da pagare / parziali" value={fullReport.riepilogo.quote_da_pagare} cls="text-amber-600" />
                  <Stat label="Ricevute" value={fullReport.riepilogo.ricevute} />
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-xs text-slate-600">
                  <b>{fullReport.riepilogo.centri} centri:</b> {fullReport.riepilogo.centri_names.join(", ")}
                </div>
                {!fullReport.dry_run && (
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2 text-xs text-emerald-800 flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" /> Importazione completata con successo.
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {!fullReport ? (
              <Button data-testid="et-full-analizza" onClick={() => runFull(true)} disabled={!fullFile || fullBusy} className="brand-gradient text-white border-0">
                {fullBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Analizza file"}
              </Button>
            ) : fullReport.dry_run ? (
              <>
                <Button variant="outline" onClick={() => setFullReport(null)}>Ricarica</Button>
                <Button data-testid="et-full-conferma" onClick={() => runFull(false)} disabled={fullBusy} className="bg-emerald-600 hover:bg-emerald-700 text-white border-0">
                  {fullBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Azzera e importa tutto"}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setFullOpen(false)}>Chiudi</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cfOpen} onOpenChange={setCfOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Importa Codici Fiscali</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
              Carica un file <b>Excel (.xlsx)</b> o <b>CSV</b> con le colonne <b>Cognome/Nome</b> (o Nominativo) e <b>Codice Fiscale</b>. L'abbinamento avviene per nome. Il CF verrà salvato sull'anagrafica del beneficiario e stampato automaticamente sulle ricevute.
            </div>
            <input data-testid="et-cf-file" type="file" accept=".xlsx,.csv"
              onChange={(e) => { setCfFile(e.target.files?.[0] || null); setCfReport(null); }}
              className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200" />

            {cfReport && (
              <div className="space-y-2 text-sm" data-testid="et-cf-report">
                <div className="grid grid-cols-2 gap-2">
                  <Stat label="Righe nel file" value={cfReport.righe_totali} />
                  <Stat label="Abbinati con CF" value={cfReport.abbinati_con_cf} cls="text-emerald-600" />
                  <Stat label="Non trovati" value={cfReport.non_trovati.length} cls={cfReport.non_trovati.length ? "text-amber-600" : ""} />
                  <Stat label="Ambigui" value={cfReport.ambigui.length} cls={cfReport.ambigui.length ? "text-red-600" : ""} />
                </div>
                {cfReport.abbinati_con_cf > 0 && (
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-emerald-200 divide-y divide-emerald-50">
                    {cfReport.abbinati.filter((a) => a.cf_nuovo).map((a) => (
                      <div key={a.ben_id} className="flex justify-between px-3 py-1.5 text-xs">
                        <span className="text-slate-700">{a.nome}</span>
                        <span className="font-mono font-medium text-emerald-700">{a.cf_nuovo}{a.cf_attuale && a.cf_attuale !== a.cf_nuovo ? " (sovrascrive)" : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
                {cfReport.non_trovati.length > 0 && (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
                    <div className="flex items-center gap-1 font-medium mb-1"><AlertTriangle className="h-3.5 w-3.5" /> Non abbinati (nome non trovato)</div>
                    {cfReport.non_trovati.join(", ")}
                  </div>
                )}
                {cfReport.ambigui.length > 0 && (
                  <div className="rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-800">
                    Ambigui (più beneficiari con lo stesso nome): {cfReport.ambigui.map((a) => a.nome_file).join(", ")}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {!cfReport ? (
              <Button data-testid="et-cf-analizza" onClick={() => runCf(true)} disabled={!cfFile || cfBusy} className="brand-gradient text-white border-0">
                {cfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Analizza file"}
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setCfReport(null)}>Ricarica</Button>
                <Button data-testid="et-cf-conferma" onClick={() => runCf(false)} disabled={cfBusy || cfReport.abbinati_con_cf === 0} className="brand-gradient text-white border-0">
                  {cfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Conferma: salva ${cfReport.abbinati_con_cf} CF`}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ETLayout>
  );
}

const Stat = ({ label, value, cls = "text-slate-900" }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
    <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`font-heading text-lg ${cls}`}>{value}</div>
  </div>
);

const Fld = ({ label, children }) => (<div className="space-y-1.5"><Label className="text-slate-600 text-sm">{label}</Label>{children}</div>);
